/**
 * Los resultados de la busqueda.
 *
 * ---------------------------------------------------------------------------
 * LOS GRUPOS SE PINTAN COMO LLEGAN
 *
 * La API no devuelve una lista: devuelve grupos por (alcance, termino de
 * entrega), separados y etiquetados, y dice por que --"para que la interfaz no
 * pueda aplanarlos sin darse cuenta"--. Asi que esta pantalla no los ordena, no
 * los mezcla, no los pagina juntos y no ofrece un "ver todo por precio". El
 * orden dentro de un grupo es del servidor; el orden entre grupos, tambien.
 *
 * Una lista plana ordenada por total afirmaria que la primera fila es la mejor
 * opcion, y eso es falso: una tarifa NO_DECLARADO tiene el total mas bajo
 * porque le faltan cargos [H17][H61][H78], y una CY y una Door no son el mismo
 * producto [H65]. UNA TABLA POR GRUPO, nunca una tabla con todo: dos tarifas de
 * grupos distintos, puestas en la misma columna de totales, se comparan solas
 * aunque nadie las haya ordenado juntas.
 *
 * LAS ADVERTENCIAS NO SE ESCONDEN. La de grupo va en su cabecera y las de la
 * tarifa en una banda bajo su fila, siempre en texto, nunca en un `title` ni
 * tras un icono. Un aviso que hay que descubrir pasando el raton no existe en
 * una pantalla que se usa con prisa.
 *
 * QUE PUEDE BAJAR AL DETALLE. Lo que describe la tarifa --literales del
 * proveedor, espacio, frecuencia, confianza, los cargos-- se abre bajo demanda.
 * Lo que decide una COMPARACION no: carrier, transito, dias libres, vigencia y
 * total tienen columna propia, porque el agente los esta enfrentando entre
 * tarifas y no leyendo una sola.
 * ---------------------------------------------------------------------------
 */

import type { Bandera, CargoTarifa, GrupoComparable, ResultadoTarifa } from '../api';
import {
  celdaPorKg,
  frecuencia as pintarFrecuencia,
  pesoTarifable,
  restriccionBulto,
  ruta as pintarRuta,
} from './aereo';
import { dato, el, literal, texto } from '../dom';
import type { Traductor } from '../i18n';
import {
  esCero,
  formatearFecha,
  formatearMoneda,
  formatearRango,
  formatearTransito,
  presentarCarrier,
  resumirDiasLibres,
  type EstadoCarrier,
  type Idioma,
} from '../formato';

/** Banderas que se pintan como aviso destacado y no como etiqueta menor.
 *  Las tres afectan a si el numero de al lado significa lo que parece. */
const BANDERAS_FUERTES: ReadonlySet<Bandera> = new Set<Bandera>([
  'ALCANCE_NO_DECLARADO',
  'SIN_FLETE_PARA_EL_EQUIPO',
  'MULTIMONEDA',
  // Las tres aereas de abajo no son matices del total: dicen que la tarifa NO
  // SE PUEDE USAR tal como esta. Un embargo con el aspecto de una etiqueta
  // menor, entre otras seis, se lee al mismo nivel que "confianza baja". [H86]
  'EMBARGO',
  'BULTO_FUERA_DE_LIMITE',
  'CONSULTA_CASO_A_CASO',
]);

/** Las que se pintan en ROJO y no en ambar: con estas la tarifa no se reserva
 *  hoy, no es que haya que leer algo antes de decidir. */
const BANDERAS_CRITICAS: ReadonlySet<Bandera> = new Set<Bandera>([
  'EMBARGO',
  'BULTO_FUERA_DE_LIMITE',
]);

export interface OpcionesResultados {
  readonly t: Traductor;
  readonly idioma: Idioma;
  readonly onCotizar: (r: ResultadoTarifa) => void;
  readonly seleccionado: string | null;
  /**
   * Si la columna de total existe.
   *
   * Es del RESPONSE entero y no de cada tarifa: la ausencia de montos es una
   * propiedad de quien pregunta --su `ve_costo`--, no de la tarifa que se mira.
   * Derivarlo por fila daria una tabla con la columna puesta o quitada segun el
   * resultado, que es exactamente lo que no pasa.
   */
  readonly veCosto: boolean;
  /**
   * La tarifa que el agente esta mirando, para que el mapa encienda su ruta.
   * `null` cuando sale de la fila.
   *
   * Es un adorno y por eso es opcional: la tabla se pinta igual sin el. Se
   * dispara con el raton y tambien con el foco --se recorre con el tabulador, y
   * el resaltado no puede ser solo para quien apunta--.
   */
  readonly onResaltar?: (tarifa: ResultadoTarifa | null) => void;
  /**
   * Si la tabla es la aerea.
   *
   * Del CRITERIO de la respuesta, no de si cada tarifa trae bloque `aereo`.
   * Derivarlo por fila daria una tabla con columnas distintas segun el
   * resultado, que es justo lo que no puede pasar: las columnas son el contrato
   * de la comparacion. Ver la cabecera de vista/aereo.ts para por que las dos
   * tablas no son la misma con un campo mas.
   */
  readonly esAereo: boolean;
}

export function pintarGrupos(
  grupos: readonly GrupoComparable[],
  o: OpcionesResultados,
): HTMLElement[] {
  return grupos.map((g) => pintarGrupo(g, o));
}

function pintarGrupo(g: GrupoComparable, o: OpcionesResultados): HTMLElement {
  const { t } = o;

  return el('section', { clase: 'panel grupo' }, [
    el('header', { clase: 'grupo-cabecera' }, [
      el('h2', {
        clase: 'grupo-titulo',
        texto: t('grupo.titulo', {
          alcance: t(`alcance.${g.alcance}`),
          termino: t(`terminoEntrega.${g.termino_entrega}`),
        }),
      }),
      el('p', { clase: 'tenue', texto: t('grupo.comparabilidad') }),
      // La advertencia de comparabilidad, en la cabecera y en texto plano.
      g.advertencia === null
        ? null
        : el('div', { clase: 'aviso aviso-alcance' }, [
            el('strong', { texto: t('grupo.advertencia') }),
            // El texto viene de la API. Es el unico sitio de la pantalla donde
            // se pinta prosa del servidor: los hallazgos que cita ([H17],
            // [H65]) son la explicacion, y reescribirla aqui la duplicaria.
            el('p', { texto: g.advertencia }),
          ]),
    ]),
    el('div', { clase: 'grupo-tabla' }, [
      el('table', { clase: 'tabla-resultados' }, [
        el('thead', {}, [cabeceraTabla(o)]),
        ...g.resultados.map((r) => pintarTarifa(r, o)),
      ]),
    ]),
  ]);
}

/**
 * El numero de columnas, para los `colspan` de las filas a lo ancho.
 *
 * Las dos tablas tienen las mismas seis salvo una: en maritimo hay dias libres
 * y en aereo hay precio por kilo. No es un cambio de nombre --los dias libres
 * en aereo vienen nulos porque se declaran por equipo [H42], y el precio por
 * kilo en maritimo no existe-- asi que la columna se sustituye entera.
 */
function columnas(o: OpcionesResultados): number {
  return o.veCosto ? 7 : 6;
}

function cabeceraTabla(o: OpcionesResultados): HTMLElement {
  const { t } = o;
  return el('tr', {}, [
    el('th', { texto: t('carrier.titulo'), attrs: { scope: 'col' } }),
    el('th', { texto: t('resultado.proveedor'), attrs: { scope: 'col' } }),
    // El precio por kilo ocupa el sitio de los dias libres. Ver `columnas`.
    o.esAereo
      ? el('th', { texto: t('aereo.porKgColumna'), attrs: { scope: 'col' } })
      : null,
    el('th', { texto: t('resultado.transito'), attrs: { scope: 'col' } }),
    o.esAereo
      ? null
      : el('th', { texto: t('resultado.diasLibres'), attrs: { scope: 'col' } }),
    el('th', { texto: t('resultado.vigencia'), attrs: { scope: 'col' } }),
    // La columna de total NO se pinta vacia ni con guiones cuando el usuario no
    // ve costos: se quita entera. Una columna "Total" con guiones parece una
    // tarifa a la que le faltan datos, y lo que pasa es otra cosa --que estos
    // montos no salieron del servidor--. Se dice arriba, con palabras, una vez.
    o.veCosto
      ? el('th', {
          clase: 'celda-total',
          texto: t('resultado.total'),
          attrs: { scope: 'col' },
        })
      : null,
    el('th', { clase: 'celda-acciones', attrs: { scope: 'col' } }, [
      // Columna de acciones: encabezado en blanco para la vista y con nombre
      // para quien la escucha.
      el('span', { clase: 'visualmente-oculto', texto: t('resultado.acciones') }),
    ]),
  ]);
}

/**
 * Una tarifa: un `<tbody>` con su fila de datos y, si hacen falta, su banda de
 * advertencias y su detalle. Ver la cabecera de `estilo/tabla-resultados.css`
 * para por que la unidad es el tbody y no el tr.
 */
function pintarTarifa(r: ResultadoTarifa, o: OpcionesResultados): HTMLElement {
  const { t, idioma } = o;

  const cuerpo = el('tbody', {
    clase: `tarifa${o.seleccionado === r.rate_record_id ? ' tarifa-elegida' : ''}`,
  });

  if (o.onResaltar !== undefined) {
    const resaltar = o.onResaltar;
    // `focusin`/`focusout` y no `focus`: burbujean desde los botones de la
    // fila, que es lo que recibe el foco al tabular.
    cuerpo.addEventListener('mouseenter', () => resaltar(r));
    cuerpo.addEventListener('mouseleave', () => resaltar(null));
    cuerpo.addEventListener('focusin', () => resaltar(r));
    cuerpo.addEventListener('focusout', () => resaltar(null));
  }

  const detalle = pintarDetalle(r, o);
  const alternar = el('button', {
    clase: 'boton boton-plano boton-menor',
    texto: t('resultado.verDetalle'),
    attrs: { type: 'button', 'aria-expanded': 'false' },
    onClick: () => {
      detalle.hidden = !detalle.hidden;
      alternar.textContent = detalle.hidden
        ? t('resultado.verDetalle')
        : t('resultado.ocultarDetalle');
      alternar.setAttribute('aria-expanded', detalle.hidden ? 'false' : 'true');
    },
  });

  cuerpo.appendChild(
    el('tr', { clase: 'fila-tarifa' }, [
      el('td', { clase: 'celda-carrier' }, [celdaCarrier(r, t)]),
      el('td', {}, [
        r.proveedor.identificado
          ? texto(r.proveedor.nombre ?? '', 'proveedor-nombre')
          : el('span', {
              clase: 'proveedor-nombre sin-identificar',
              texto: t('resultado.proveedorNoIdentificado'),
            }),
      ]),
      // El precio por kilo, que es la cifra que se compara en aereo. Va antes
      // del transito a proposito: es lo primero que se mira despues de saber
      // quien opera.
      o.esAereo
        ? el('td', { clase: 'celda-por-kg' }, [
            r.aereo === undefined
              ? el('span', { clase: 'tenue', texto: t('resultado.noDeclarado') })
              : celdaPorKg(r, r.aereo, t, idioma),
          ])
        : null,
      el('td', { clase: 'celda-numero' }, [celdaTransito(r, t)]),
      o.esAereo ? null : el('td', { clase: 'celda-dias' }, [celdaDiasLibres(r, t)]),
      el('td', { clase: 'celda-vigencia' }, [pintarVigencia(r, t, idioma)]),
      o.veCosto ? el('td', { clase: 'celda-total' }, [celdaTotal(r, idioma)]) : null,
      el('td', { clase: 'celda-acciones' }, [
        alternar,
        el('button', {
          clase: 'boton boton-primario boton-menor',
          texto: t('resultado.cotizar'),
          attrs: { type: 'button' },
          onClick: () => o.onCotizar(r),
        }),
      ]),
    ]),
  );

  // LA BANDA AEREA VA ANTES QUE LOS AVISOS Y SIEMPRE VISIBLE.
  //
  // No es detalle: es lo que hace comparable una tarifa aerea. El peso que se
  // cobra con su explicacion, la ruta con sus escalas y los dias que opera son
  // las tres cosas que en aereo deciden, y meterlas detras de "Ver cargos"
  // dejaria la fila diciendo un precio por kilo sin decir a cuantos kilos ni
  // por donde. Ver la cabecera de vista/aereo.ts.
  const banda = pintarBandaAerea(r, o);
  if (banda !== null) cuerpo.appendChild(banda);

  const avisos = pintarAvisos(r, o);
  if (avisos !== null) cuerpo.appendChild(avisos);
  cuerpo.appendChild(detalle);

  return cuerpo;
}

/**
 * La anatomia aerea bajo la fila: peso, ruta y frecuencia.
 *
 * `null` fuera del aereo. No se pinta vacia en maritimo --seria una fila de
 * huecos por tarifa-- y no se pinta desde el bloque `aereo` de cada resultado
 * sin mas: se pide `o.esAereo` primero, para que una respuesta mezclada no
 * meta media tabla con banda y media sin ella.
 */
function pintarBandaAerea(r: ResultadoTarifa, o: OpcionesResultados): HTMLElement | null {
  if (!o.esAereo || r.aereo === undefined) return null;
  const { t } = o;
  const a = r.aereo;

  return el('tr', { clase: 'fila-aerea' }, [
    el('td', { attrs: { colspan: String(columnas(o)) } }, [
      el('div', { clase: 'aereo-banda' }, [
        el('div', { clase: 'aereo-bloque aereo-bloque-peso' }, [
          el('h4', { texto: t('aereo.pesoTarifable') }),
          pesoTarifable(a, t),
        ]),
        el('div', { clase: 'aereo-bloque aereo-bloque-ruta' }, [
          el('h4', { texto: t('aereo.ruta') }),
          pintarRuta(a, t),
        ]),
        el('div', { clase: 'aereo-bloque aereo-bloque-frecuencia' }, [
          el('h4', { texto: t('resultado.frecuencia') }),
          pintarFrecuencia(a, t),
        ]),
      ]),
    ]),
  ]);
}

// ---------------------------------------------------------------------------
// Celdas
// ---------------------------------------------------------------------------

/**
 * El carrier con su estado.
 *
 * El BADGE va siempre, incluido el IDENTIFICADO. Un badge que solo aparece
 * cuando algo va mal obliga a leer su ausencia, y la ausencia de una etiqueta
 * no se ve: en una columna donde todas las filas dicen algo, la que dice
 * "pendiente de clave" salta. El identificado va en gris y los otros tres en
 * ambar, asi que el color separa "normal" de "miralo" sin que ninguno dependa
 * solo del color: los cuatro llevan su texto.
 */
function celdaCarrier(r: ResultadoTarifa, t: Traductor): HTMLElement {
  const carrier = presentarCarrier(r.carrier);

  return el('div', { clase: 'carrier-celda' }, [
    el('div', { clase: 'carrier' }, [
      // [H44][H56] Nunca en blanco: "carrier no revelado" y una celda vacia son
      // cosas distintas. Tres casos, y el del medio es el que se olvida:
      //
      //   nombre del catalogo   -> se pinta el nombre
      //   sin nombre, con literal -> se pinta EL LITERAL como nombre
      //   ni una cosa ni la otra  -> se pinta el estado, en ambar
      //
      // El segundo es un IDENTIFICADO que todavia no esta en el catalogo de
      // carriers: `presentarCarrier` ya decide que su literal es mejor dato que
      // ninguno, y pintar ahi un hueco --con el literal repetido al lado en
      // pequeño-- dejaba la columna con la casilla del nombre en blanco, que es
      // justo lo que [H44] dice que no puede pasar.
      carrier.clave !== null
        ? el('span', { clase: 'carrier-nombre carrier-sin-nombre' }, [
            texto(t(carrier.clave)),
          ])
        : carrier.nombre !== null
          ? texto(carrier.nombre, 'carrier-nombre')
          : el('span', { clase: 'carrier-nombre' }, [
              literal(carrier.literal, 'literal literal-destacado'),
            ]),
      badgeCarrier(carrier.estado, t),
      // El literal del proveedor acompaña siempre que exista, sin traducir.
      // Salvo cuando YA es lo que se esta pintando como nombre, arriba: dos
      // veces el mismo texto en la misma celda no es conservar el literal, es
      // repetirlo. Pasa en dos sitios --cuando el literal ES el nombre, y
      // cuando el nombre normalizado coincide con el literal-- y los dos se
      // descuentan aqui.
      carrier.clave !== null ||
      (carrier.nombre !== null && carrier.nombre !== r.carrier.literal)
        ? literal(r.carrier.literal)
        : null,
    ]),
    r.carrier.tipo_contrato === null && r.carrier.servicio_loop === null
      ? null
      : el('div', { clase: 'carrier-detalle' }, [
          r.carrier.tipo_contrato === null
            ? null
            : el('span', {
                clase: 'tenue',
                texto: `${t('carrier.contrato')}: ${r.carrier.tipo_contrato}`,
              }),
          literal(r.carrier.servicio_loop),
        ]),
  ]);
}

function badgeCarrier(estado: EstadoCarrier, t: Traductor): HTMLElement {
  const clase = estado === 'IDENTIFICADO' ? 'badge' : 'badge badge-atencion';
  return el('span', {
    clase,
    texto: t(`estadoCarrier.${estado}`),
    // La version larga explica que significa cada estado. El badge es corto
    // porque va en una columna; el `title` no lleva nada que no este tambien
    // escrito en la pantalla cuando importa --el nombre del carrier ya dice
    // "carrier no revelado" en su sitio--, asi que no esconde informacion.
    attrs: { title: t(`carrier.${estado}`) },
  });
}

function celdaTransito(r: ResultadoTarifa, t: Traductor): HTMLElement {
  const dias = formatearTransito(r.transito.min_dias, r.transito.max_dias);
  if (dias === null) {
    // Ausente se dice con palabras, nunca con un cero ni con una celda en
    // blanco [H59]. Corto porque el encabezado de la columna ya dice de que
    // dato se esta hablando: dentro de la columna "Transito", "No declarado" es
    // la frase entera.
    return el('span', { clase: 'tenue', texto: t('resultado.noDeclarado') });
  }
  return el('span', { clase: 'cifra', texto: `${dias} ${t('resultado.dias')}` });
}

function celdaDiasLibres(r: ResultadoTarifa, t: Traductor): HTMLElement {
  // [H58] Demora y detencion, cada uno con su nombre. En columna propia, que es
  // donde se comparan: una tarifa 30 USD mas cara con siete dias libres mas
  // puede ser la barata.
  const dias = resumirDiasLibres(r.dias_libres);
  if (dias.length === 0) {
    return el('span', { clase: 'tenue', texto: t('resultado.noDeclarado') });
  }
  return el(
    'span',
    { clase: 'dias-libres' },
    dias.map((d) =>
      el('span', {
        clase: `dia-libre dia-libre-${d.tipo}`,
        texto: `${String(d.dias)} ${t('resultado.dias')} ${t(`resultado.${d.tipo}`)}`,
      }),
    ),
  );
}

function celdaTotal(r: ResultadoTarifa, idioma: Idioma): HTMLElement {
  const total = r.total;
  const moneda = total?.moneda ?? null;

  // `veCosto` dice que la columna existe; que ESTA tarifa traiga total es otra
  // cosa. Se comprueba igual en vez de afirmarlo con un `!`.
  if (total === undefined) return el('span', { clase: 'total-oculto', texto: '' });

  return el('div', {}, [
    el('span', {
      clase: 'total-monto',
      // [H48] Dos cotas cuando algun cargo vino en rango. No se colapsa.
      texto: formatearRango(total.costo_min, total.costo_max, moneda, idioma),
    }),
    // [H23][Invariante 2] Un profit share es dinero a nuestro favor. Se muestra
    // aparte del costo, nunca restado dentro de el.
    esCero(total.ingreso)
      ? null
      : el('span', {
          clase: 'total-ingreso',
          texto: `+ ${formatearMoneda(total.ingreso, moneda, idioma)}`,
        }),
    ...(r.totales_por_moneda ?? [])
      .filter((x) => x.moneda !== moneda)
      .map((x) =>
        el('span', {
          clase: 'totales-otros',
          texto: formatearRango(x.costo_min, x.costo_max, x.moneda, idioma),
        }),
      ),
  ]);
}

function pintarVigencia(r: ResultadoTarifa, t: Traductor, idioma: Idioma): HTMLElement {
  // [H39][Invariante 3] Una tarifa atada a un ETD muere cuando zarpa ese buque.
  // Se pinta como ETD y no como "vigente hasta": no es lo mismo.
  if (r.vigencia.tipo === 'ETD_ESPECIFICO' && r.vigencia.etd !== null) {
    return el('span', {
      clase: 'vigencia-etd',
      texto: `${t('resultado.etd')} ${formatearFecha(r.vigencia.etd, idioma)}`,
    });
  }
  return el('span', {
    texto: `${t('resultado.hasta')} ${formatearFecha(r.vigencia.hasta, idioma)}`,
  });
}

// ---------------------------------------------------------------------------
// Banda de advertencias
// ---------------------------------------------------------------------------

/**
 * Las advertencias de la tarifa, en una fila a lo ancho bajo sus datos.
 *
 * Devuelve `null` cuando no hay ninguna: una tarifa limpia no gasta una fila en
 * decir que no tiene nada que decir.
 */
function pintarAvisos(r: ResultadoTarifa, o: OpcionesResultados): HTMLElement | null {
  const { t } = o;
  const fuertes = r.banderas.filter((b) => BANDERAS_FUERTES.has(b));
  const menores = r.banderas.filter((b) => !BANDERAS_FUERTES.has(b));

  // [H86] El texto del proveedor por bandera operativa, para pegarlo al aviso
  // que produjo. La bandera dice QUE pasa y el literal dice COMO lo dijo el
  // proveedor, que es lo que el agente lee antes de llamarle.
  // `?? []` por si la API es mas vieja que la pantalla: un despliegue a medias
  // manda la respuesta sin el campo, y una lista de resultados que se cae
  // entera por una advertencia que no llego es peor que una sin literales. Es
  // la misma precaucion que toma `coordenada` en vista/mapa.ts.
  const literales = new Map(
    (r.advertencias_operativas ?? []).map((a) => [a.bandera as Bandera, a.literal]),
  );

  if (fuertes.length === 0 && menores.length === 0) return null;

  return el('tr', { clase: 'fila-avisos' }, [
    el('td', { attrs: { colspan: String(columnas(o)) } }, [
      ...fuertes.map((b) =>
        el(
          'div',
          {
            // Rojo para lo que impide reservar, ambar para lo que hay que leer
            // antes de decidir. El color no es lo unico: los dos llevan su
            // texto entero.
            clase: `aviso aviso-fuerte${BANDERAS_CRITICAS.has(b) ? ' aviso-critico' : ''}`,
          },
          [
            el('p', { texto: t(`bandera.${b}`) }),
            // El literal del proveedor, sin traducir, debajo de su aviso.
            literal(literales.get(b) ?? null),
            // Por cuanto falla, cuando lo sabemos. Las cifras cambian la
            // decision: 2,45 contra un maximo de 2,4 se pregunta, 6 no.
            b === 'BULTO_FUERA_DE_LIMITE' ? incumplimientos(r, t) : null,
          ],
        ),
      ),
      // Las menores tambien quedan a la vista: son las que dicen "hay cargos
      // que dependen de una condicion", que es justo lo que hay que leer ANTES
      // de cotizar [Invariante 12][H25]. Compactas, pero no escondidas.
      menores.length === 0
        ? null
        : el(
            'div',
            { clase: 'senales' },
            menores.map((b) =>
              el('span', { clase: 'senal' }, [
                el('span', { texto: t(`bandera.${b}`) }),
                literal(literales.get(b) ?? null),
              ]),
            ),
          ),
    ]),
  ]);
}

/** Los dos numeros de cada incumplimiento de bulto. [H85] */
function incumplimientos(r: ResultadoTarifa, t: Traductor): HTMLElement | null {
  const lista = r.aereo?.aptitud.incumplimientos ?? [];
  if (lista.length === 0) return null;
  return el(
    'ul',
    { clase: 'aereo-incumplimientos' },
    lista.map((i) =>
      el('li', {
        texto: t(`aereo.incumple.${i.motivo}`, {
          declarado: i.declarado,
          limite: i.limite,
        }),
      }),
    ),
  );
}

// ---------------------------------------------------------------------------
// Detalle
// ---------------------------------------------------------------------------

function pintarDetalle(r: ResultadoTarifa, o: OpcionesResultados): HTMLTableRowElement {
  const { t, idioma } = o;

  /** Los limites por bulto, solo cuando la tarifa es aerea y los declara. */
  const bloqueBulto = (x: ResultadoTarifa): HTMLElement | null =>
    x.aereo === undefined ? null : restriccionBulto(x.aereo, t);

  const notas = [
    ['resultado.restriccion', r.restriccion_mercancia],
    ['resultado.condicionComercial', r.condicion_comercial],
    ['resultado.notaRevision', r.nota_revision],
  ] as const;
  const conNota = notas.filter(([, v]) => v !== null && v.trim() !== '');

  const fila = el('tr', { clase: 'fila-detalle', attrs: { hidden: 'hidden' } }, [
    el('td', { attrs: { colspan: String(columnas(o)) } }, [
      el('div', { clase: 'detalle-bloques' }, [
        el('div', { clase: 'detalle-datos' }, [
          r.espacio.total === null && r.espacio.disponible === null
            ? null
            : dato(
                t('resultado.espacio'),
                `${String(r.espacio.disponible ?? r.espacio.total)} ${t('resultado.disponible')}`,
              ),
          // En aereo la frecuencia ya esta arriba, interpretada y con su
          // literal: repetirla aqui la enseñaria dos veces en la misma fila.
          r.frecuencia_literal === null || o.esAereo
            ? null
            : dato(t('resultado.frecuencia'), literal(r.frecuencia_literal)),
          r.confianza === null ? null : dato(t('resultado.confianza'), r.confianza),
        ]),
        conNota.length === 0
          ? null
          : el(
              'div',
              { clase: 'tarifa-literales' },
              conNota.map(([clave, v]) =>
                dato(t(clave), literal(v), 'dato dato-literal'),
              ),
            ),
        // Los limites por pieza de la tarifa y el veredicto contra la carga.
        // El veredicto tambien esta arriba, en la banda de avisos, cuando falla:
        // aqui van los NUMEROS que lo sostienen, que es material de lectura y no
        // de comparacion. [H85]
        bloqueBulto(r),
        el('div', { clase: 'cargos' }, [
          el('h4', { texto: t('resultado.cargos') }),
          tablaCargos(r.cargos, t, idioma, false, o.veCosto),
        ]),
        r.cargos_fuera_del_total.length === 0
          ? null
          : el('div', { clase: 'cargos-fuera' }, [
              el('h4', { texto: t('resultado.cargosFuera') }),
              tablaCargos(r.cargos_fuera_del_total, t, idioma, true, o.veCosto),
            ]),
      ]),
    ]),
  ]);

  return fila;
}

/**
 * La tabla de cargos. `veCosto` decide si existe la columna de monto.
 *
 * No se pinta vacia ni con guiones: se quita la columna entera. Una columna
 * "Total" con cuatro guiones parece una tarifa a la que le faltan datos, y lo
 * que pasa es otra cosa --que este usuario no recibe esos montos--. Lo demas de
 * cada fila no cambia: codigo, literal, tramo, inclusion, direccion y
 * pass-through son terminos, y son lo que dice QUE lleva la tarifa.
 */
function tablaCargos(
  cargos: readonly CargoTarifa[],
  t: Traductor,
  idioma: Idioma,
  conMotivo: boolean,
  veCosto: boolean,
): HTMLElement {
  if (cargos.length === 0) {
    return el('p', { clase: 'tenue', texto: t('app.sinResultados') });
  }

  const cabecera = el('tr', {}, [
    el('th', { texto: t('cotizador.codigo') }),
    el('th', { texto: t('cotizador.concepto') }),
    el('th', { texto: t('resultado.tramo') }),
    veCosto ? el('th', { clase: 'celda-monto', texto: t('cotizador.total') }) : null,
    conMotivo ? el('th', { texto: t('resultado.motivoFuera') }) : null,
  ]);

  const filas = cargos.map((c) =>
    el('tr', { clase: c.direccion === 'INGRESO' ? 'fila-ingreso' : undefined }, [
      el('td', {}, [el('code', { texto: c.codigo_cargo })]),
      el('td', {}, [
        el('div', { texto: c.nombre }),
        // El literal del proveedor, sin traducir. Es lo que el agente reconoce
        // cuando compara contra el correo.
        literal(c.descripcion_literal),
        c.inclusion === 'ADICIONAL'
          ? null
          : // [Invariante 1][H62] Un INCLUIDO no se suma. Que se vea.
            el('span', {
              clase: 'marca-inclusion',
              texto: t(`inclusion.${c.inclusion}`),
            }),
        c.direccion === 'INGRESO'
          ? el('span', { clase: 'marca-ingreso', texto: t('direccion.INGRESO') })
          : null,
        c.pass_through
          ? el('span', { clase: 'marca-passthrough', texto: 'pass-through' })
          : null,
      ]),
      el('td', { texto: t(`tramo.${c.tramo}`) }),
      veCosto
        ? el('td', {
            clase: 'celda-monto',
            texto:
              c.monto != null
                ? formatearMoneda(c.monto, c.moneda, idioma)
                : formatearRango(
                    c.monto_min ?? null,
                    c.monto_max ?? null,
                    c.moneda,
                    idioma,
                  ),
          })
        : null,
      conMotivo
        ? el('td', {}, [
            el('div', {
              texto: t(`motivoFuera.${c.motivo_fuera_del_total ?? 'RANGO_SIN_COTAS'}`),
            }),
            ...c.condiciones.map((k) =>
              el('div', {
                clase: 'condicion',
                // El umbral tal como lo declaro el proveedor. En F1 lo lee el
                // agente y decide [Invariante 12][H25].
                texto: [
                  k.variable,
                  k.umbral_desde === null ? null : `≥ ${k.umbral_desde}`,
                  k.umbral_hasta === null ? null : `≤ ${k.umbral_hasta}`,
                  k.unidad,
                ]
                  .filter((x) => x !== null)
                  .join(' '),
              }),
            ),
          ])
        : null,
    ]),
  );

  return el('table', { clase: 'tabla-cargos' }, [
    el('thead', {}, [cabecera]),
    el('tbody', {}, filas),
  ]);
}
